import axiosClient from "utils/axios";
import { commaSeparatedEnums, positiveInt } from "utils/list-query";
import { ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  EnquiryIntent,
  EnquiryStatus,
} from "features/Enquiries/constants/enquiry.constants";
import {
  AddEnquiryRequest,
  Enquiry,
  type EnquirySelectOption,
  GetEnquiriesResponse,
  UpdateEnquiryRequest,
} from "features/Enquiries/types/enquiry.types";

const ENQUIRY_LIST_STATUSES = Object.values(EnquiryStatus);
const ENQUIRY_LIST_INTENTS = Object.values(EnquiryIntent);

function normalizeEnquiryOptionRow(
  row: Record<string, unknown>,
): EnquirySelectOption {
  const value = Number(row.value);
  return {
    value: Number.isFinite(value) ? value : 0,
    label: String(row.label ?? ""),
    enquiryIntent:
      (row.enquiryIntent as string | undefined) ??
      (row.enquiry_intent as string | undefined),
    title:
      typeof row.title === "string" && row.title.trim()
        ? row.title.trim()
        : undefined,
  };
}

export type GetEnquiriesListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string | string[];
  enquiryIntent?: string | string[];
  clientId?: number | string;
  priorityId?: number | string;
  sourceChannelId?: number | string;
};

export const getEnquiries = async ({
  page,
  limit,
  search,
  status,
  enquiryIntent,
  clientId,
  priorityId,
  sourceChannelId,
}: GetEnquiriesListParams = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<GetEnquiriesResponse>>(
      "/enquiries",
      {
        params: {
          page,
          limit,
          search: search?.trim() || undefined,
          status: commaSeparatedEnums(status, ENQUIRY_LIST_STATUSES),
          enquiryIntent: commaSeparatedEnums(
            enquiryIntent,
            ENQUIRY_LIST_INTENTS,
          ),
          clientId: positiveInt(clientId),
          priorityId: positiveInt(priorityId),
          sourceChannelId: positiveInt(sourceChannelId),
        },
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching enquiries : ", err);
    throw err;
  }
};

export const getEnquiryOptions = async ({
  query,
  limit,
  page,
}: {
  query?: string;
  limit?: number;
  page?: number;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/enquiries/options`,
      {
        params: {
          query,
          limit,
          page,
        },
      },
    );

    const body = response.data;
    const inner = body.data ?? { rows: [], hasMore: false };
    const rows = (inner.rows ?? []).map((r) =>
      normalizeEnquiryOptionRow(r as unknown as Record<string, unknown>),
    );

    return {
      ...body,
      data: {
        ...inner,
        rows,
      },
    };
  } catch (err) {
    console.warn("Error fetching enquiry options : ", err);
    throw err;
  }
};

export const createEnquiry = async (payload: AddEnquiryRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<AddEnquiryRequest>>(
      "/enquiries",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating enquiry : ", err);
    throw err;
  }
};

export const updateEnquiry = async (id: number, payload: AddEnquiryRequest) => {
  try {
    const response = await axiosClient.put<ApiResponse<UpdateEnquiryRequest>>(
      `/enquiries/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating enquiry : ", err);
    throw err;
  }
};

export const getEnquiry = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<Enquiry>>(
      `/enquiries/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching enquiry : ", err);
    throw err;
  }
};

export const deleteEnquiry = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<Enquiry>>(
      `/enquiries/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting enquiry : ", err);
    throw err;
  }
};

